import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, CircularProgress, alpha, Checkbox, FormControlLabel } from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import BadgeIcon from '@mui/icons-material/Badge';
import { T } from '../theme/tokens';
import { SynButton } from './ds';
import { api, apiFetch, describeApiError } from '../api/client';
import { notifyIdentityChanged } from '../auth/identity';
import { describeStaffLoginFailure, StaffLoginFailure } from '../auth/staffLoginError';

const STORAGE_KEY = 'spartis_access_v2';
const EXPIRY_DAYS = 30;
// How often an unlocked device silently re-verifies its stored code against the
// server. This makes a rotated SPARTIS_ACCESS_CODE lock out old devices on their
// next load (after the throttle window) without a manual storage-key bump.
const RECHECK_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

function isGranted(): boolean {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return false;
        const { granted, expires, staff } = JSON.parse(raw);
        if (!granted) return false;
        if (expires && Date.now() > expires) {
            localStorage.removeItem(STORAGE_KEY);
            return false;
        }
        // Preview-code grants (no `staff` flag) are only honored where the
        // preview path itself is enabled (dev/pitch builds). In production the
        // caller never consults isGranted() at all — the server session is the
        // only unlock — but clear stale preview grants defensively anyway.
        if (!PREVIEW_ACCESS_ENABLED && !staff) {
            localStorage.removeItem(STORAGE_KEY);
            return false;
        }
        return true;
    } catch {
        return false;
    }
}

function grantAccess(code: string) {
    const expires = Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ granted: true, expires, code, lastChecked: Date.now() }),
    );
}

/**
 * Grant unlock for a signed-in staff member WITHOUT a preview code. The entry
 * carries `staff: true` and no `code`, so the self-healing preview re-check
 * skips it (nothing to re-verify — the real auth is the HttpOnly staff session
 * cookie, enforced per-request by the backend). Expiry still bounds the shell
 * unlock; staff whose session lapses simply see the sign-in again on the gate.
 */
// One-click demo buttons render only in dev/pitch builds. The env flag is
// baked at build time; the backend endpoint additionally 404s in production,
// so even a mis-built bundle cannot demo-login against a real deployment.
const DEMO_LOGIN_ENABLED =
    process.env.NODE_ENV !== 'production' || process.env.REACT_APP_PITCH_MODE === '1';

// The public research-preview access-code path is a dev/pitch convenience only.
// In real production builds the ONLY way past the gate is an invited sign-in
// (email + one-time invitation code -> HttpOnly session cookie). Preview-code
// grants stored by older builds are ignored and cleared (see isGranted).
//
// RETIREMENT DECISION (research-preview code): the code shared during the
// preview period is retired. `SPARTIS_ACCESS_CODE` is UNSET in production and
// the backend hard-disables POST /auth/verify-code there (404), so no
// production surface accepts the preview code even with a stale bundle. The
// dev/pitch preview path is KEPT deliberately as a friction-free demo unlock;
// it is UI-shell-only and never grants API access.
const PREVIEW_ACCESS_ENABLED =
    process.env.NODE_ENV !== 'production' || process.env.REACT_APP_PITCH_MODE === '1';

function grantStaffAccess() {
    const expires = Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ granted: true, expires, staff: true }),
    );
}

/**
 * Silently re-verify the stored access code against the server so a rotated
 * SPARTIS_ACCESS_CODE locks out previously-unlocked devices automatically.
 *
 * Returns `true` when access should be kept, `false` when the device must be
 * sent back to the gate. Throttled by RECHECK_INTERVAL_MS and FAILS OPEN on any
 * transient/network/server error so a backend blip never locks everyone out.
 */
async function revalidateGrant(): Promise<boolean> {
    let parsed: { code?: string; lastChecked?: number } | null = null;
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return true;
        parsed = JSON.parse(raw);
    } catch {
        return true;
    }
    if (!parsed) return true;
    const code = (parsed.code || '').trim();
    // Without a stored code we cannot re-verify; leave the (expiry-bounded) grant.
    if (!code) return true;
    // Throttle: only hit the server once per RECHECK_INTERVAL_MS.
    if (parsed.lastChecked && Date.now() - parsed.lastChecked < RECHECK_INTERVAL_MS) {
        return true;
    }
    try {
        // Migrated to the shared API client (Synterra A3): 'public' context (this
        // is the unauthenticated preview gate). We use the low-level apiFetch so
        // the fail-open semantics stay explicit — a server error (!res.ok) or a
        // thrown network error both keep the device unlocked; only an explicit
        // { success:false } re-locks it.
        const res = await apiFetch('/auth/verify-code', {
            method: 'POST',
            json: { code },
            context: 'public',
        });
        // Server error -> fail open, retry next window.
        if (!res.ok) return true;
        const data = await res.json();
        if (data && data.success) {
            try {
                localStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify({ ...parsed, lastChecked: Date.now() }),
                );
            } catch {}
            return true;
        }
        // Explicit non-success: the code no longer matches (e.g. rotated). Lock out.
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {}
        return false;
    } catch {
        // Network/transient/server error -> fail open.
        return true;
    }
}

// NOTE: AccessGate intentionally owns ONLY the public research-preview grant
// (`spartis_access_v2`). The admin/internal API credential is a separate,
// staff-entered code managed in `config.ts` (`spartis_admin_code`). Do not derive
// an admin code from this preview grant — that conflation let any preview user
// call internal admin endpoints.

/**
 * Strict identity-contract check for /site-users/me. Both staff and invited
 * site users get `authenticated: true` + a non-empty string user_id from the
 * backend; anything else (proxy error bodies, unexpected 200s) stays locked.
 */
function isValidSessionBody(d: any): boolean {
    return !!d && d.authenticated === true
        && typeof d.user_id === 'string' && d.user_id.length > 0;
}

interface Props {
    children: React.ReactNode;
    /**
     * Test seam only: overrides the build-time preview flag so the production
     * lockdown branch can be exercised under jest (NODE_ENV=test). Never pass
     * this in app code.
     */
    previewAccessEnabled?: boolean;
}

export default function AccessGate({ children, previewAccessEnabled = PREVIEW_ACCESS_ENABLED }: Props) {
    // Production lockdown: localStorage is NEVER the initial authorization
    // decision in real deployments — a forged `staff:true` record must not
    // mount the shell even briefly. Production starts locked and unlocks only
    // after /site-users/me positively validates the HttpOnly session cookie.
    const [unlocked, setUnlocked]   = useState(() => previewAccessEnabled && isGranted());
    const [code, setCode]           = useState('');
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState('');
    const [shake, setShake]         = useState(false);
    const [fadeIn, setFadeIn]       = useState(false);
    // Staff sign-in mode on the gate (invited staff/developer reviewers should
    // never need the research-preview code — see Task: external access flow).
    const [staffMode, setStaffMode]   = useState(!previewAccessEnabled);
    const [staffEmail, setStaffEmail] = useState('');
    const [staffCode, setStaffCode]   = useState('');
    const [agreeConfidentiality, setAgreeConfidentiality] = useState(false);
    const [staffError, setStaffError] = useState<StaffLoginFailure | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!unlocked && !staffMode) setTimeout(() => inputRef.current?.focus(), 300);
    }, [unlocked, staffMode]);

    // A device may already hold a valid staff session cookie (e.g. the preview
    // grant expired but the HttpOnly session did not). Let them straight in.
    useEffect(() => {
        if (unlocked) return;
        let cancelled = false;
        (async () => {
            try {
                const resp = await apiFetch('/site-users/me', { context: 'public' });
                if (!resp.ok) return;
                const d = await resp.json();
                // Any valid session (staff OR invited site user) gets past the
                // shell gate — per-request authorization stays with the backend.
                if (!cancelled && isValidSessionBody(d)) {
                    grantStaffAccess();
                    setUnlocked(true);
                }
            } catch { /* stay on the gate */ }
        })();
        return () => { cancelled = true; };
    }, [unlocked]);

    // Self-healing gate: when unlocked, silently re-verify the stored code so a
    // rotated SPARTIS_ACCESS_CODE sends old devices back to the gate on next load.
    useEffect(() => {
        if (!unlocked) return;
        let cancelled = false;
        revalidateGrant().then(stillValid => {
            if (!cancelled && !stillValid) setUnlocked(false);
        });
        return () => { cancelled = true; };
    }, [unlocked]);

    if (unlocked) return <>{children}</>;

    const triggerShake = (msg: string) => {
        setError(msg);
        setShake(true);
        setTimeout(() => setShake(false), 600);
    };

    const handleStaffSubmit = async () => {
        // Redeeming an invitation code = first activation: the confidentiality
        // agreement is required (also enforced server-side).
        if (staffCode.trim() && !agreeConfidentiality) {
            setStaffError({
                kind: 'other',
                message: 'Please agree to the confidentiality notice.',
                hint: 'Check the box confirming your invitation code and sign-in details are for you only.',
            });
            return;
        }
        if (!staffEmail.trim()) {
            setStaffError({
                kind: 'other',
                message: 'Enter the email address you were invited with.',
                hint: 'Staff accounts are created by invitation — use the email your invitation was sent to.',
            });
            return;
        }
        setLoading(true);
        setStaffError(null);
        try {
            // Same flow StaffAccessControl uses inside the shell: mints the
            // HttpOnly role-aware staff session cookie. No passwords — first
            // sign-in uses the one-time invitation code, later ones email only.
            await api.post(
                '/site-users/verify-login',
                {
                    email: staffEmail.trim(),
                    invitation_code: staffCode.trim() || undefined,
                    accept_confidentiality: staffCode.trim() ? agreeConfidentiality : undefined,
                },
                { context: 'public' },
            );
            grantStaffAccess();
            notifyIdentityChanged();
            setFadeIn(true);
            setTimeout(() => setUnlocked(true), 400);
        } catch (err) {
            // Classified failure copy — explains WHAT failed (no account /
            // wrong code / expired / rate-limited) and the recovery path.
            setStaffError(describeStaffLoginFailure(err));
        } finally {
            setLoading(false);
        }
    };

    // One-click demo sign-in: POST /auth/demo-login mints a REAL role session
    // for a self-provisioned demo user. Backend hard-404s in production, so
    // this is dev/pitch friction relief only — not an access path.
    const handleDemoLogin = async (role: string) => {
        setLoading(true);
        setError('');
        try {
            await api.post('/auth/demo-login', { role }, { context: 'public' });
            grantStaffAccess();
            notifyIdentityChanged();
            setFadeIn(true);
            setTimeout(() => setUnlocked(true), 400);
        } catch (err) {
            triggerShake(describeApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!code.trim()) return;
        setLoading(true);
        setError('');
        try {
            // Migrated to the shared API client (Synterra A3): 'public' context
            // (unauthenticated preview gate). verify-code always returns 200 with
            // { success }; a thrown error means the server was unreachable.
            const res = await apiFetch('/auth/verify-code', {
                method: 'POST',
                json: { code: code.trim() },
                context: 'public',
            });
            const data = await res.json();
            if (data.success) {
                grantAccess(code.trim());
                setFadeIn(true);
                setTimeout(() => setUnlocked(true), 400);
            } else {
                triggerShake('Incorrect access code. Please try again.');
                setCode('');
                setTimeout(() => inputRef.current?.focus(), 100);
            }
        } catch {
            triggerShake('Could not reach the server. Please refresh and try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(160deg, ${T.color.teal[900]} 0%, ${T.color.teal[800]} 45%, #0A4A58 100%)`,
            position: 'relative',
            overflow: 'hidden',
            opacity: fadeIn ? 0 : 1,
            transition: 'opacity 0.4s ease',
        }}>
            {/* Atmospheric radial glow */}
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(42,155,176,0.18) 0%, transparent 70%)',
            }} />
            {/* Subtle grid texture */}
            <Box sx={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
                backgroundSize: '32px 32px',
            }} />

            {/* Gate card */}
            <Box
                sx={{
                    position: 'relative', zIndex: 1,
                    width: '100%', maxWidth: 420,
                    mx: 2,
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid rgba(255,255,255,0.12)`,
                    borderRadius: '20px',
                    p: { xs: 4, sm: 5 },
                    boxShadow: '0 24px 80px rgba(0,0,0,0.45)',
                    animation: shake ? 'spartis-shake 0.5s ease' : 'none',
                    '@keyframes spartis-shake': {
                        '0%, 100%': { transform: 'translateX(0)' },
                        '15%': { transform: 'translateX(-8px)' },
                        '45%': { transform: 'translateX(8px)' },
                        '65%': { transform: 'translateX(-5px)' },
                        '85%': { transform: 'translateX(4px)' },
                    },
                }}
            >
                {/* Logo */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box sx={{
                        width: 44, height: 44, borderRadius: '50%',
                        background: T.gradient.sphere,
                        boxShadow: `0 0 24px ${alpha(T.color.teal[500], 0.5)}`,
                        flexShrink: 0,
                    }} />
                    <Box>
                        <Typography sx={{
                            color: '#fff', fontSize: '1.35rem',
                            letterSpacing: '0.08em', fontWeight: 800, lineHeight: 1.1,
                        }}>
                            SYNTERRA
                        </Typography>
                        <Typography sx={{
                            color: 'rgba(255,255,255,0.45)',
                            fontSize: '0.65rem', letterSpacing: '0.12em', textTransform: 'uppercase',
                        }}>
                            Health
                        </Typography>
                    </Box>
                </Box>

                {/* Badge */}
                <Box sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.75,
                    px: 1.5, py: 0.5,
                    borderRadius: '20px',
                    background: alpha(T.color.magenta[500], 0.15),
                    border: `1px solid ${alpha(T.color.magenta[500], 0.35)}`,
                    mb: 3,
                }}>
                    <Box sx={{
                        width: 6, height: 6, borderRadius: '50%',
                        bgcolor: T.color.magenta[500],
                        boxShadow: `0 0 6px ${T.color.magenta[500]}`,
                    }} />
                    <Typography sx={{
                        color: T.color.magenta[400],
                        fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                    }}>
                        Research Preview
                    </Typography>
                </Box>

                {staffMode ? (
                <>
                {/* Staff sign-in (invited staff / developer reviewers) */}
                <Typography sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 700, mb: 0.75 }}>
                    Staff sign-in
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', mb: 3.5, lineHeight: 1.6 }}>
                    Sign in with the email Synterra Health invited you with. The invitation
                    code is only needed the first time you sign in.
                </Typography>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Email address"
                    type="email"
                    value={staffEmail}
                    autoComplete="off"
                    onChange={e => { setStaffEmail(e.target.value); setStaffError(null); }}
                    onKeyDown={e => e.key === 'Enter' && handleStaffSubmit()}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            color: '#fff',
                            fontSize: '0.95rem',
                            borderRadius: '10px',
                            background: 'rgba(255,255,255,0.06)',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                            '&.Mui-focused fieldset': { borderColor: T.color.teal[500] },
                        },
                        '& input::placeholder': { color: 'rgba(255,255,255,0.25)' },
                    }}
                />
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Invitation code (first sign-in only)"
                    value={staffCode}
                    autoComplete="off"
                    onChange={e => { setStaffCode(e.target.value.toUpperCase()); setStaffError(null); }}
                    onKeyDown={e => e.key === 'Enter' && handleStaffSubmit()}
                    sx={{
                        mb: staffError ? 1.5 : 2.5,
                        '& .MuiOutlinedInput-root': {
                            color: '#fff',
                            fontSize: '0.95rem',
                            fontFamily: 'monospace',
                            borderRadius: '10px',
                            background: 'rgba(255,255,255,0.06)',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                            '&.Mui-focused fieldset': { borderColor: T.color.teal[500] },
                        },
                        '& input::placeholder': { color: 'rgba(255,255,255,0.25)' },
                    }}
                />
                {staffCode.trim().length > 0 && (
                    <Box sx={{ mb: 2, p: 1.5, textAlign: 'left', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px' }}>
                        <FormControlLabel
                            sx={{ alignItems: 'flex-start', m: 0 }}
                            control={(
                                <Checkbox
                                    size="small"
                                    checked={agreeConfidentiality}
                                    onChange={e => { setAgreeConfidentiality(e.target.checked); setStaffError(null); }}
                                    sx={{ mt: -0.5, color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: T.color.teal[400] } }}
                                    inputProps={{ 'aria-label': 'Agree to the confidentiality notice' } as React.InputHTMLAttributes<HTMLInputElement>}
                                />
                            )}
                            label={(
                                <Typography sx={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>
                                    I understand this invitation and my login details are for me only,
                                    and I agree not to copy, forward, share, or screenshot my
                                    invitation code, sign-in details, or platform content.
                                </Typography>
                            )}
                        />
                    </Box>
                )}
                {staffError && (
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ color: '#ff8a9a', fontSize: '0.8rem', fontWeight: 600 }}>
                            {staffError.message}
                        </Typography>
                        <Typography sx={{
                            color: 'rgba(255,255,255,0.55)', fontSize: '0.75rem',
                            mt: 0.5, lineHeight: 1.5,
                        }}>
                            {staffError.hint}
                        </Typography>
                    </Box>
                )}
                <SynButton
                    variant="primary"
                    fullWidth
                    onClick={handleStaffSubmit}
                    disabled={loading || !staffEmail.trim()}
                    startIcon={loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <BadgeIcon />}
                    sx={{ py: 1.4, fontSize: '0.9rem', borderRadius: '10px' }}
                >
                    {loading ? 'Signing in…' : 'Sign in'}
                </SynButton>
                {previewAccessEnabled && (
                <Typography
                    role="button"
                    tabIndex={0}
                    onClick={() => { setStaffMode(false); setStaffError(null); }}
                    onKeyDown={e => e.key === 'Enter' && setStaffMode(false)}
                    sx={{
                        color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem',
                        textAlign: 'center', mt: 2.5, cursor: 'pointer',
                        textDecoration: 'underline', textUnderlineOffset: 3,
                        '&:hover': { color: 'rgba(255,255,255,0.7)' },
                    }}
                >
                    Have a research-preview access code instead?
                </Typography>
                )}
                </>
                ) : (
                <>
                {/* Headline */}
                <Typography sx={{ color: '#fff', fontSize: '1.1rem', fontWeight: 700, mb: 0.75 }}>
                    Enter your access code
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', mb: 3.5, lineHeight: 1.6 }}>
                    The Synterra Health platform is in active development. Enter your code to access the research preview.
                </Typography>

                {/* Input */}
                <TextField
                    inputRef={inputRef}
                    fullWidth
                    variant="outlined"
                    placeholder="Access code"
                    value={code}
                    onChange={e => { setCode(e.target.value); setError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                    type="password"
                    autoComplete="off"
                    sx={{
                        mb: error ? 1.5 : 2.5,
                        '& .MuiOutlinedInput-root': {
                            color: '#fff',
                            fontSize: '1rem',
                            letterSpacing: '0.15em',
                            borderRadius: '10px',
                            background: 'rgba(255,255,255,0.06)',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                            '&.Mui-focused fieldset': { borderColor: T.color.teal[500] },
                        },
                        '& input::placeholder': { color: 'rgba(255,255,255,0.25)', letterSpacing: '0.05em' },
                    }}
                />

                {/* Error */}
                {error && (
                    <Typography sx={{
                        color: '#ff8a9a', fontSize: '0.8rem', mb: 2,
                        display: 'flex', alignItems: 'center', gap: 0.75,
                    }}>
                        {error}
                    </Typography>
                )}

                {/* Submit */}
                <SynButton
                    variant="primary"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={loading || !code.trim()}
                    startIcon={loading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <LockOpenIcon />}
                    sx={{ py: 1.4, fontSize: '0.9rem', borderRadius: '10px' }}
                >
                    {loading ? 'Verifying…' : 'Access Platform'}
                </SynButton>

                {/* Invited staff / developer reviewers skip the preview code */}
                <Typography
                    role="button"
                    tabIndex={0}
                    onClick={() => { setStaffMode(true); setError(''); }}
                    onKeyDown={e => e.key === 'Enter' && setStaffMode(true)}
                    sx={{
                        color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem',
                        textAlign: 'center', mt: 2.5, cursor: 'pointer',
                        textDecoration: 'underline', textUnderlineOffset: 3,
                        '&:hover': { color: 'rgba(255,255,255,0.7)' },
                    }}
                >
                    Invited staff member? Sign in with your email
                </Typography>

                {/* One-click demo sign-in (dev/pitch builds only; the backend
                    endpoint 404s in production so these can never work there) */}
                {DEMO_LOGIN_ENABLED && (
                    <Box sx={{ mt: 3.5, pt: 2.5, borderTop: '1px solid rgba(255,255,255,0.10)' }}>
                        <Typography sx={{
                            color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem',
                            textAlign: 'center', mb: 1.5, letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                        }}>
                            One-click demo sign-in
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                            {([
                                ['internal_admin', 'Admin'],
                                ['evaluator', 'Evaluator'],
                                ['ta_provider', 'Support Provider'],
                            ] as const).map(([role, label]) => (
                                <SynButton
                                    key={role}
                                    variant="ghost"
                                    disabled={loading}
                                    onClick={() => handleDemoLogin(role)}
                                    sx={{
                                        fontSize: '0.78rem', px: 1.75, py: 0.6, borderRadius: '8px',
                                        color: 'rgba(255,255,255,0.8)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        '&:hover': { borderColor: 'rgba(255,255,255,0.45)' },
                                    }}
                                >
                                    {label}
                                </SynButton>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Footer */}
                <Typography sx={{
                    color: 'rgba(255,255,255,0.2)', fontSize: '0.7rem',
                    textAlign: 'center', mt: 3,
                }}>
                    Access is granted for 30 days per device.
                </Typography>
                </>
                )}
            </Box>
        </Box>
    );
}
